import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { ProcessBadge } from '../components/ProcessBadge'

interface MetaIdeaRisk {
  description?: string
  severity?: string
  [key: string]: unknown
}

interface MetaIdeaMember {
  id: string
  idea_id: string
  code: string
  title: string
  similarity: number | null
  manual: boolean
}

interface MetaIdeaDetail {
  id: string
  title: string
  summary: string | null
  common_problem: string | null
  aggregated_pros: string[] | null
  aggregated_cons: string[] | null
  aggregated_risks: MetaIdeaRisk[] | null
  member_count: number
  status: string
  generated_by: string
  promoted_to_idea_id: string | null
  process_tags?: string[]
  created_at: string
  updated_at: string
  members: MetaIdeaMember[]
}

export const Route = createFileRoute('/meta-ideas_/$id')({
  beforeLoad: async () => {
    const s = useAuth.getState()
    if (s.loading) await s.load()
    const u = useAuth.getState().user
    if (!u) throw redirect({ to: '/login' })
    if (u.role === 'user') throw redirect({ to: '/my-ideas' })
  },
  component: MetaIdeaDetailPage,
})

function MetaIdeaDetailPage() {
  const { id } = Route.useParams()
  const user = useAuth((s) => s.user)
  const isAdmin = user?.role === 'admin'
  const qc = useQueryClient()
  const [opError, setOpError] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['meta-idea', id],
    queryFn: () => api<MetaIdeaDetail>(`/api/meta-ideas/${id}`),
  })

  const regenerate = useMutation({
    mutationFn: () =>
      api(`/api/admin/meta-ideas/${id}/regenerate-summary`, { method: 'POST' }),
    onSuccess: () => {
      setOpError(null)
      void qc.invalidateQueries({ queryKey: ['meta-idea', id] })
      void qc.invalidateQueries({ queryKey: ['meta-ideas'] })
    },
    onError: () => setOpError('Не удалось перегенерировать summary'),
  })

  const promote = useMutation({
    mutationFn: () => api(`/api/admin/meta-ideas/${id}/promote`, { method: 'POST' }),
    onSuccess: () => {
      setOpError(null)
      void qc.invalidateQueries({ queryKey: ['meta-idea', id] })
      void qc.invalidateQueries({ queryKey: ['meta-ideas'] })
    },
    onError: () => setOpError('Не удалось продвинуть в инициативу (нужен summary)'),
  })

  if (isLoading) return <p className="p-8 text-sm text-zinc-500">Загрузка…</p>
  if (error || !data) {
    return (
      <div className="max-w-3xl mx-auto p-8">
        <p className="text-sm text-red-600">Мета-идея не найдена</p>
        <Link to="/meta-ideas" className="text-sm text-blue-600 hover:underline">
          ← К мета-идеям
        </Link>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-8 space-y-6">
      <div>
        <Link to="/meta-ideas" className="text-sm text-blue-600 hover:underline">
          ← К мета-идеям
        </Link>
      </div>

      <section className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-6">
        <div className="flex items-start justify-between gap-4">
          <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
            {data.title}
          </h1>
          <span className="text-xs px-2 py-0.5 rounded-md bg-zinc-100 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300 whitespace-nowrap">
            {data.member_count} идей
          </span>
        </div>
        <p className="mt-1 text-xs text-zinc-500">
          {data.generated_by === 'manual'
            ? '✋ создано вручную'
            : data.generated_by === 'llm'
              ? '🤖 GLM авто-кластер'
              : '🤖 AI кластер'}
          {' · '}
          {data.status === 'active' && 'активна'}
          {data.status === 'archived' && 'архив'}
          {data.status === 'promoted' && '✅ продвинута в инициативу'}
        </p>
        {data.process_tags && data.process_tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {data.process_tags.map((code) => (
              <ProcessBadge key={code} code={code} size="sm" />
            ))}
          </div>
        )}

        {data.common_problem && (
          <div className="mt-4">
            <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              Общая проблема
            </h2>
            <p className="mt-1 text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap">
              {data.common_problem}
            </p>
          </div>
        )}
        {data.summary && (
          <div className="mt-4">
            <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              Summary
            </h2>
            <p className="mt-1 text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap">
              {data.summary}
            </p>
          </div>
        )}

        {(data.aggregated_pros?.length ||
          data.aggregated_cons?.length ||
          data.aggregated_risks?.length) && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            {data.aggregated_pros && data.aggregated_pros.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-green-700 dark:text-green-400 mb-1">
                  ✓ Плюсы
                </h3>
                <ul className="space-y-1 list-disc list-inside text-zinc-700 dark:text-zinc-300">
                  {data.aggregated_pros.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              </div>
            )}
            {data.aggregated_cons && data.aggregated_cons.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-red-700 dark:text-red-400 mb-1">
                  ✗ Минусы
                </h3>
                <ul className="space-y-1 list-disc list-inside text-zinc-700 dark:text-zinc-300">
                  {data.aggregated_cons.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </div>
            )}
            {data.aggregated_risks && data.aggregated_risks.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-orange-700 dark:text-orange-400 mb-1">
                  ⚠️ Риски
                </h3>
                <ul className="space-y-1 text-zinc-700 dark:text-zinc-300">
                  {data.aggregated_risks.map((r, i) => (
                    <li key={i}>
                      {r.severity && (
                        <span
                          className={
                            r.severity === 'high'
                              ? 'text-red-600'
                              : r.severity === 'medium'
                                ? 'text-amber-600'
                                : 'text-zinc-500'
                          }
                        >
                          [{r.severity}]{' '}
                        </span>
                      )}
                      {r.description ?? JSON.stringify(r)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {isAdmin && (
          <div className="mt-6 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => regenerate.mutate()}
              disabled={regenerate.isPending}
              className="px-3 py-1.5 border border-zinc-300 dark:border-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-700 disabled:opacity-50 rounded-md text-sm text-zinc-700 dark:text-zinc-300"
            >
              {regenerate.isPending ? '🤖 Генерирую…' : '🤖 Регенерировать summary'}
            </button>
            <button
              type="button"
              onClick={() => promote.mutate()}
              disabled={promote.isPending || data.status === 'promoted'}
              className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-md text-sm"
            >
              {data.status === 'promoted'
                ? '✅ Уже продвинута'
                : promote.isPending
                  ? 'Продвигаю…'
                  : '🚀 Продвинуть в инициативу'}
            </button>
            {data.promoted_to_idea_id && (
              <Link
                to="/ideas/$id"
                params={{ id: data.promoted_to_idea_id }}
                className="px-3 py-1.5 text-sm text-blue-600 hover:underline"
              >
                → к инициативе
              </Link>
            )}
          </div>
        )}
        {opError && <p className="mt-3 text-sm text-red-600">{opError}</p>}
      </section>

      <section className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-6">
        <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-3">
          Участники кластера ({data.members.length})
        </h2>
        {data.members.length === 0 ? (
          <p className="text-sm text-zinc-500">пусто</p>
        ) : (
          <ul className="divide-y divide-zinc-200 dark:divide-zinc-700">
            {data.members.map((m) => (
              <li key={m.id}>
                <Link
                  to="/ideas/$id"
                  params={{ id: m.idea_id }}
                  className="flex items-center gap-3 py-2 hover:bg-zinc-50 dark:hover:bg-zinc-900 rounded px-2"
                >
                  <span className="text-xs font-mono text-zinc-500 w-24 shrink-0">
                    {m.code}
                  </span>
                  <span className="flex-1 text-sm text-zinc-900 dark:text-zinc-100 truncate">
                    {m.title}
                  </span>
                  {m.similarity !== null && (
                    <span className="text-xs text-zinc-500 font-mono">
                      {(m.similarity * 100).toFixed(0)}%
                    </span>
                  )}
                  {m.manual && (
                    <span className="text-xs text-zinc-500">✋</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
